import { Board } from '../game/model/Board';
import { directionVectors, isTurn } from '../game/model/cells';
import type { LevelDefinition } from '../game/model/types';
import type { ComputedDifficultyMetrics } from './types';
import { bothOppositeSides, sideAdjacencyMask } from './interest';

interface StructuralMetrics {
  readonly peelDepth: number;
  readonly minPeelWidth: number;
  readonly longRangeBlockCount: number;
}

interface InitialInterestMetrics {
  readonly chainRunCount: number;
  readonly trapCount: number;
  readonly embeddedTrapCount: number;
}

const oppositeDirection = {
  up: 'down',
  right: 'left',
  down: 'up',
  left: 'right',
} as const;

function connectedComponents(
  indexes: readonly number[],
  adjacent: (leftIndex: number, rightIndex: number) => boolean,
): readonly (readonly number[])[] {
  const remaining = new Set(indexes);
  const components: number[][] = [];
  while (remaining.size > 0) {
    const first = remaining.values().next().value as number;
    remaining.delete(first);
    const component = [first];
    for (let cursor = 0; cursor < component.length; cursor += 1) {
      const current = component[cursor];
      if (current === undefined) continue;
      for (const candidate of [...remaining]) {
        if (adjacent(current, candidate)) {
          remaining.delete(candidate);
          component.push(candidate);
        }
      }
    }
    components.push(component);
  }
  return components;
}

export function computeInitialInterestMetrics(level: LevelDefinition): InitialInterestMetrics {
  const board = new Board(level);
  const indexesById = new Map(level.arrows.map((arrow, index) => [arrow.id, index]));
  const removable = new Set(board.removableArrowIds.flatMap((id) => {
    const index = indexesById.get(id);
    return index === undefined ? [] : [index];
  }));
  const occupancy = new Int32Array(level.width * level.height);
  level.arrows.forEach((arrow, arrowIndex) => arrow.cells.forEach(({ x, y }) => {
    occupancy[y * level.width + x] = arrowIndex + 1;
  }));
  const sideAdjacent = level.arrows.map(() => new Set<number>());
  level.arrows.forEach((arrow, arrowIndex) => {
    const horizontal = arrow.direction === 'left' || arrow.direction === 'right';
    const offsets = horizontal
      ? [{ x: 0, y: -1 }, { x: 0, y: 1 }]
      : [{ x: -1, y: 0 }, { x: 1, y: 0 }];
    for (const cell of arrow.cells) {
      for (const offset of offsets) {
        const x = cell.x + offset.x;
        const y = cell.y + offset.y;
        if (x < 0 || x >= level.width || y < 0 || y >= level.height) continue;
        const neighborIndex = (occupancy[y * level.width + x] ?? 0) - 1;
        const neighbor = level.arrows[neighborIndex];
        if (neighborIndex < 0 || neighborIndex === arrowIndex || neighbor === undefined) continue;
        const neighborHorizontal = neighbor.direction === 'left' || neighbor.direction === 'right';
        if (horizontal === neighborHorizontal) {
          sideAdjacent[arrowIndex]?.add(neighborIndex);
        }
      }
    }
  });
  const areSideAdjacent = (leftIndex: number, rightIndex: number): boolean =>
    sideAdjacent[leftIndex]?.has(rightIndex) ?? false;
  let chainRunCount = 0;
  for (const direction of ['up', 'right', 'down', 'left'] as const) {
    const indexes = [...removable].filter((index) =>
      level.arrows[index]?.direction === direction);
    chainRunCount += connectedComponents(indexes, areSideAdjacent)
      .filter((component) => component.length >= 4).length;
  }

  const trapIndexes = new Set<number>();
  const embeddedTrapIndexes = new Set<number>();
  for (const direction of ['up', 'right', 'down', 'left'] as const) {
    const indexes = level.arrows.map((_, index) => index).filter((index) =>
      level.arrows[index]?.direction === direction);
    const chainLikeComponents = connectedComponents(
      indexes,
      areSideAdjacent,
    ).filter((component) => component.length >= 3
      && component.filter((index) => removable.has(index)).length >= 2);
    const reversedBlocked = level.arrows.flatMap((arrow, arrowIndex) =>
      !removable.has(arrowIndex) && arrow.direction === oppositeDirection[direction]
        ? [arrowIndex]
        : []);
    for (const trapIndex of reversedBlocked) {
      let isTrap = false;
      let isEmbedded = false;
      for (const component of chainLikeComponents) {
        const localMembers = component.filter((memberIndex) =>
          areSideAdjacent(memberIndex, trapIndex));
        if (localMembers.length === 0) continue;
        const hasNearbySecondReverse = reversedBlocked.some((otherIndex) =>
          otherIndex !== trapIndex && localMembers.some((memberIndex) =>
            areSideAdjacent(memberIndex, otherIndex)));
        if (!hasNearbySecondReverse) {
          isTrap = true;
          const adjacencyMask = localMembers.reduce((mask, memberIndex) => {
            const member = level.arrows[memberIndex];
            const trap = level.arrows[trapIndex];
            return member === undefined || trap === undefined
              ? mask
              : mask | sideAdjacencyMask(member.cells, trap.cells, direction);
          }, 0);
          if (adjacencyMask === bothOppositeSides) isEmbedded = true;
        }
      }
      if (isTrap) trapIndexes.add(trapIndex);
      if (isEmbedded) embeddedTrapIndexes.add(trapIndex);
    }
  }
  return {
    chainRunCount,
    trapCount: trapIndexes.size,
    embeddedTrapCount: embeddedTrapIndexes.size,
  };
}

function countTurns(level: LevelDefinition): number {
  return level.arrows.reduce((total, arrow) => {
    let turns = 0;
    for (let index = 2; index < arrow.cells.length; index += 1) {
      const first = arrow.cells[index - 2];
      const middle = arrow.cells[index - 1];
      const last = arrow.cells[index];
      if (first !== undefined && middle !== undefined && last !== undefined
        && isTurn(first, middle, last)) {
        turns += 1;
      }
    }
    return total + turns;
  }, 0);
}

// Reimplements Board's exit-ray blocking on an occupancy grid because the
// Board API costs O(arrows x cells) per query, which is too slow for the
// 5,000-level batch. Semantic equivalence with Board is pinned by the
// "matches Board-based parallel peeling" test in difficulty.test.ts.
function computeStructuralMetrics(level: LevelDefinition): StructuralMetrics {
  const occupancy = new Int32Array(level.width * level.height);
  level.arrows.forEach((arrow, arrowIndex) => {
    for (const cell of arrow.cells) {
      occupancy[cell.y * level.width + cell.x] = arrowIndex + 1;
    }
  });

  const firstBlockerDistance = (arrowIndex: number): number | null => {
    const arrow = level.arrows[arrowIndex];
    const tip = arrow?.cells.at(-1);
    if (arrow === undefined || tip === undefined) {
      return null;
    }
    const step = directionVectors[arrow.direction];
    let distance = 1;
    for (
      let x = tip.x + step.x, y = tip.y + step.y;
      x >= 0 && x < level.width && y >= 0 && y < level.height;
      x += step.x, y += step.y, distance += 1
    ) {
      const occupant = occupancy[y * level.width + x];
      if (occupant !== 0 && occupant !== arrowIndex + 1) {
        return distance;
      }
    }
    return null;
  };

  let longRangeBlockCount = 0;
  for (let arrowIndex = 0; arrowIndex < level.arrows.length; arrowIndex += 1) {
    const blockerDistance = firstBlockerDistance(arrowIndex);
    // A blocker four cells ahead leaves three empty cells after the tip.
    if (blockerDistance !== null && blockerDistance >= 4) {
      longRangeBlockCount += 1;
    }
  }

  const remaining = new Set(level.arrows.map((_, arrowIndex) => arrowIndex));
  let peelDepth = 0;
  let minPeelWidth = Number.POSITIVE_INFINITY;
  while (remaining.size > 0) {
    const removable = [...remaining].filter(
      (arrowIndex) => firstBlockerDistance(arrowIndex) === null,
    );
    if (removable.length === 0) {
      throw new Error('Board cannot be emptied by simultaneous peel rounds.');
    }
    peelDepth += 1;
    minPeelWidth = Math.min(minPeelWidth, removable.length);
    for (const arrowIndex of removable) {
      remaining.delete(arrowIndex);
      const arrow = level.arrows[arrowIndex];
      if (arrow !== undefined) {
        for (const cell of arrow.cells) {
          occupancy[cell.y * level.width + cell.x] = 0;
        }
      }
    }
  }

  return {
    peelDepth,
    minPeelWidth: Number.isFinite(minPeelWidth) ? minPeelWidth : 0,
    longRangeBlockCount,
  };
}

export function computeMetrics(
  level: LevelDefinition,
  solutionOrder: readonly string[],
): ComputedDifficultyMetrics {
  const board = new Board(level);
  if (solutionOrder.length !== board.totalCount) {
    throw new Error('Solution order must contain every arrow exactly once.');
  }
  const indexesById = new Map(level.arrows.map((arrow, index) => [arrow.id, index]));
  const occupancy = new Int32Array(level.width * level.height);
  const active = new Uint8Array(level.arrows.length);
  active.fill(1);
  level.arrows.forEach((arrow, arrowIndex) => arrow.cells.forEach(({ x, y }) => {
    occupancy[y * level.width + x] = arrowIndex + 1;
  }));
  const canRemove = (arrowIndex: number): boolean => {
    const arrow = level.arrows[arrowIndex];
    const tip = arrow?.cells.at(-1);
    if (arrow === undefined || tip === undefined || active[arrowIndex] === 0) {
      return false;
    }
    const step = directionVectors[arrow.direction];
    for (
      let x = tip.x + step.x, y = tip.y + step.y;
      x >= 0 && x < level.width && y >= 0 && y < level.height;
      x += step.x, y += step.y
    ) {
      const occupant = (occupancy[y * level.width + x] ?? 0) - 1;
      if (occupant >= 0 && occupant !== arrowIndex && active[occupant] !== 0) {
        return false;
      }
    }
    return true;
  };
  const choiceCounts: number[] = [];
  for (const arrowId of solutionOrder) {
    let choiceCount = 0;
    for (let arrowIndex = 0; arrowIndex < level.arrows.length; arrowIndex += 1) {
      if (active[arrowIndex] !== 0 && canRemove(arrowIndex)) choiceCount += 1;
    }
    choiceCounts.push(choiceCount);
    const arrowIndex = indexesById.get(arrowId);
    if (arrowIndex === undefined || active[arrowIndex] === 0) {
      throw new Error(`Solution arrow "${arrowId}" was not removable (missing).`);
    }
    if (!canRemove(arrowIndex)) {
      throw new Error(`Solution arrow "${arrowId}" was not removable (blocked).`);
    }
    active[arrowIndex] = 0;
    for (const { x, y } of level.arrows[arrowIndex]?.cells ?? []) {
      occupancy[y * level.width + x] = 0;
    }
  }
  if (active.some((value) => value !== 0)) {
    throw new Error('Solution order did not remove every arrow.');
  }

  const arrowCount = level.arrows.length;
  const totalCells = level.arrows.reduce((sum, arrow) => sum + arrow.cells.length, 0);
  const structuralMetrics = computeStructuralMetrics(level);
  const initialInterestMetrics = computeInitialInterestMetrics(level);
  return {
    arrowCount,
    averageArrowLength: arrowCount === 0 ? 0 : totalCells / arrowCount,
    totalTurnCount: countTurns(level),
    initialRemovableCount: choiceCounts[0] ?? 0,
    ...structuralMetrics,
    ...initialInterestMetrics,
    minChoiceCount: choiceCounts.length === 0 ? 0 : Math.min(...choiceCounts),
    averageChoiceCount: choiceCounts.length === 0
      ? 0
      : choiceCounts.reduce((sum, count) => sum + count, 0) / choiceCounts.length,
  };
}
